import classNames from 'classnames';
import {
    useState,
    useRef,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useContext,
    type CSSProperties,
    type ChangeEvent,
} from 'react';
import { useLatest } from '../../hooks/useLatest';
import PlayBigIcon from '../../assets/play-big.svg?react';
import { FJDetailsPlaybackContext } from '../FJDetailsPage/FJDetailsPlaybackContext';
import FJLoading from '../../common/components/FJLoading/FJLoading';
import FJCoreConfig from '../../core/FJCoreConfig';
import FJVideoController from './FJVideoController';
import { logShortCardInteraction } from '../../util/FJLongToShortEa';
import styles from './FJVideoPreviewPlayer.module.less';

/** 与 `FJProjectStore.createPreviewApi` 对齐，供详情拆条绑定 `FJDetailManager` */
interface VideoPreviewPlayerApi {
    setVideoCanvas: (canvas: HTMLCanvasElement) => void;
    setSubtitleView: (view: HTMLElement | null) => void;
    setTimeData: (startTime: number, endTime: number) => void;
    seek: (time: number) => Promise<void>;
    /** 点击播放时在同步栈内调用，占位手势以便带声解码（见 `FJDetailManager.primePlaybackForGesture`） */
    primePlaybackForGesture?: () => void;
    play: (cb?: (t: number) => void, pauseCb?: (t: number) => void) => void | Promise<void>;
    pause: () => Promise<void>;
    stopLocalPlaybackState?: () => void;
    drawVideoCanvas: () => void;
    resizeView: () => void;
    getCurrentTime: () => number;
    setBufferingListener?: (fn: ((loading: boolean) => void) | null) => void;
}

function formatTime(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

const PREVIEW_MAX_SIZE = FJCoreConfig.VIDEO_PREVIEW_MAX_LONG_EDGE;
/** 播放中进度条/时间文案更新间隔（ms），降低 React 重绘频率；底层 `FJVideoPlayBase` 已按 FPS 节流绘制 */
const PLAYBACK_UI_THROTTLE_MS = 100;
const AT_SEGMENT_START_EPS = 0.08;

function getPreviewSize(
    ratio: string,
    sourceWidth: number,
    sourceHeight: number,
    maxPreviewSide: number = PREVIEW_MAX_SIZE,
): { width: number; height: number } {
    let w: number, h: number;
    if (ratio === 'original' && sourceWidth > 0 && sourceHeight > 0) {
        w = sourceWidth;
        h = sourceHeight;
    } else {
        const parts = ratio.split(':').map(Number);
        w = parts[0] || 16;
        h = parts[1] || 9;
    }
    const maxSide = Math.max(w, h);
    if (maxSide <= 0) return { width: maxPreviewSide, height: maxPreviewSide };
    const scale = maxPreviewSide / maxSide;
    return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

/** 详情短视频卡片埋点上下文（与 `logShortCardInteraction` 字段一致） */
export interface VideoPreviewShortCardEaContext {
    project_id: string;
    short_id: string;
    position: number;
    viral_score?: number;
    video_duration?: number;
}

interface VideoPreviewPlayerProps {
    /** 总时长（秒） */
    duration: number;
    /** 裁剪开始时间（秒） */
    startTime: number;
    /** 裁剪结束时间（秒） */
    endTime: number;
    /** 预览比例，如 'original' | '9:16' */
    previewRatio: string;
    /** 源视频宽高，用于 original 比例 */
    sourceWidth: number;
    sourceHeight: number;
    /** 与 `FJProjectStore.createPreviewApi` 返回值一致的播放器适配层 */
    previewApi: VideoPreviewPlayerApi;
    /** 预览长边像素上限，默认 360；详情竖版可提高以按高度铺满 */
    previewMaxSide?: number;
    /**
     * 详情片头静态封面（`videoData.thumbnailUrl`）：停在本段起点且未播放时叠在画布下展示，避免多卡共享解码器首帧相同；
     * 播放或 seek 离开片头后切换为画布解码画面。
     */
    thumbnailUrl?: string;
    /**
     * `fill`：铺满父级（详情卡片），视频 contain 居中；
     * `intrinsic`：固定比例小窗（导出 Demo 等）
     */
    layout?: 'intrinsic' | 'fill';
    /**
     * 详情页多卡片：与片段 id 一致；在 `FJDetailsPlaybackProvider` 下仅允许一路播放，
     * 其它实例在「非本人」为当前 playing 时会自动 pause。
     */
    playbackInstanceId?: string;
    /** 将「暂停当前预览」交给父级（点赞/工具条点击前需直接 pause，仅靠 clearPlayback 无法停当前卡） */
    onPauseControlReady?: (pause: () => void) => void;
    /** 用户点击播放时回调（如退出多选） */
    onPlayStart?: () => void;
    /** 详情页：用户对播放器的播放/暂停操作记 `click`，自然播放到本段结束记 `finish` */
    shortCardEa?: VideoPreviewShortCardEaContext;
}

export default function FJVideoPreviewPlayer({
    duration,
    startTime,
    endTime,
    previewRatio,
    sourceWidth,
    sourceHeight,
    previewApi,
    previewMaxSide,
    thumbnailUrl = '',
    layout = 'intrinsic',
    playbackInstanceId,
    onPauseControlReady,
    onPlayStart,
    shortCardEa,
}: VideoPreviewPlayerProps) {
    const detailsPlayback = useContext(FJDetailsPlaybackContext);
    const exclusivePlayback = Boolean(playbackInstanceId && detailsPlayback);

    const adapter = previewApi;
    const adapterRef = useLatest(adapter);
    const maxPreview = previewMaxSide ?? PREVIEW_MAX_SIZE;
    const isFillLayout = layout === 'fill';

    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [fitBox, setFitBox] = useState<{ w: number; h: number } | null>(null);
    const [areaSize, setAreaSize] = useState({ w: 0, h: 0 });
    const [posterLoadError, setPosterLoadError] = useState(false);
    const [playbackStarting, setPlaybackStarting] = useState(false);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const subtitleContainerRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const videoAreaRef = useRef<HTMLDivElement>(null);
    const lastPlaybackUiFlushRef = useRef(0);
    const playRequestSeqRef = useRef(0);
    const resizeRafRef = useRef<number | null>(null);
    /** 本裁剪段自然播放到末尾只上报一次 `finish` */
    const segmentFinishLoggedRef = useRef(false);

    const segmentDuration = Math.max(0, endTime - startTime);
    const posterSrc = thumbnailUrl?.trim() ?? '';

    const logShortCardUserClick = useCallback(() => {
        if (!shortCardEa?.project_id || !shortCardEa.short_id) return;
        logShortCardInteraction({
            action_type: 'click',
            project_id: shortCardEa.project_id,
            short_id: shortCardEa.short_id,
            position: shortCardEa.position,
            viral_score: shortCardEa.viral_score ?? 0,
            video_duration: shortCardEa.video_duration ?? segmentDuration,
        });
    }, [shortCardEa, segmentDuration]);

    useEffect(() => {
        segmentFinishLoggedRef.current = false;
    }, [playbackInstanceId, startTime, endTime, shortCardEa?.short_id]);

    const previewDims = useMemo(
        () => getPreviewSize(previewRatio, sourceWidth, sourceHeight, maxPreview),
        [previewRatio, sourceWidth, sourceHeight, maxPreview],
    );
    const { width: contentW, height: contentH } = previewDims;

    const onBuffering = useCallback((loading: boolean) => {
        setIsBuffering(loading);
    }, []);

    const scheduleResizeView = useCallback(() => {
        if (resizeRafRef.current != null) return;
        resizeRafRef.current = requestAnimationFrame(() => {
            resizeRafRef.current = null;
            adapterRef.current.resizeView();
        });
    }, [adapterRef]);

    const requestExclusivePlay = useCallback(() => {
        if (exclusivePlayback && playbackInstanceId && detailsPlayback) {
            detailsPlayback.requestPlay(playbackInstanceId);
        }
    }, [exclusivePlayback, playbackInstanceId, detailsPlayback]);

    const notifyExclusivePaused = useCallback(() => {
        if (exclusivePlayback && playbackInstanceId && detailsPlayback) {
            detailsPlayback.notifyPaused(playbackInstanceId);
        }
    }, [exclusivePlayback, playbackInstanceId, detailsPlayback]);

    useEffect(() => {
        setPosterLoadError(false);
    }, [posterSrc]);

    useLayoutEffect(() => {
        if (!isFillLayout) return;
        const el = videoAreaRef.current;
        if (!el) return;
        const read = () => {
            const r = el.getBoundingClientRect();
            setAreaSize({ w: Math.floor(r.width), h: Math.floor(r.height) });
        };
        read();
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, [isFillLayout, duration, isFullscreen]);

    const { fitW, fitH } = useMemo(() => {
        if (!isFillLayout || areaSize.w < 4 || areaSize.h < 4) {
            return { fitW: contentW, fitH: contentH };
        }
        const scale = Math.min(areaSize.w / contentW, areaSize.h / contentH);
        return {
            fitW: Math.max(2, Math.floor(contentW * scale)),
            fitH: Math.max(2, Math.floor(contentH * scale)),
        };
    }, [isFillLayout, areaSize.w, areaSize.h, contentW, contentH]);

    // 绑定画布与字幕、裁剪区间；窗口尺寸变化时 rAF 合并 resizeView
    useEffect(() => {
        if (duration <= 0 || !canvasRef.current) return;
        const canvas = canvasRef.current;
        adapter.setVideoCanvas(canvas);
        adapter.setSubtitleView(subtitleContainerRef.current);
        adapter.setTimeData(startTime, endTime);
        adapter.seek(0).then(() => adapter.drawVideoCanvas());
        queueMicrotask(() => setCurrentTime(0));

        const onWinResize = () => scheduleResizeView();
        window.addEventListener('resize', onWinResize);
        return () => {
            window.removeEventListener('resize', onWinResize);
            adapter.setSubtitleView(null);
            if (resizeRafRef.current != null) {
                cancelAnimationFrame(resizeRafRef.current);
                resizeRafRef.current = null;
            }
        };
    }, [adapter, duration, startTime, endTime, scheduleResizeView]);

    // 画布尺寸变化时 resizeView（与窗口监听共用同一节流函数）
    useEffect(() => {
        if (duration <= 0 || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const ro = new ResizeObserver(scheduleResizeView);
        ro.observe(canvas);
        return () => {
            ro.disconnect();
            if (resizeRafRef.current != null) {
                cancelAnimationFrame(resizeRafRef.current);
                resizeRafRef.current = null;
            }
        };
    }, [adapter, duration, previewRatio, maxPreview, scheduleResizeView]);

    useEffect(() => {
        adapter.setBufferingListener?.(onBuffering);
        return () => adapter.setBufferingListener?.(null);
    }, [adapter, onBuffering]);

    const handlePlay = useCallback(() => {
        if (duration <= 0) return;
        onPlayStart?.();
        const requestSeq = ++playRequestSeqRef.current;
        segmentFinishLoggedRef.current = false;
        const prevPlayingId = detailsPlayback?.playingId ?? null;
        requestExclusivePlay();
        adapter.primePlaybackForGesture?.();
        adapter.setBufferingListener?.(onBuffering);
        setPlaybackStarting(true);
        setIsBuffering(false);
        let isFirstPlaybackTick = true;

        void (async () => {
            try {
                await adapter.pause();
                if (prevPlayingId && prevPlayingId !== playbackInstanceId) {
                    await adapter.seek(0);
                    setCurrentTime(0);
                }
                await adapter.play(
                    (time: number) => {
                        if (
                            shortCardEa?.project_id &&
                            shortCardEa.short_id &&
                            segmentDuration > 0 &&
                            !segmentFinishLoggedRef.current &&
                            time >= segmentDuration - 0.06
                        ) {
                            segmentFinishLoggedRef.current = true;
                            logShortCardInteraction({
                                action_type: 'finish',
                                project_id: shortCardEa.project_id,
                                short_id: shortCardEa.short_id,
                                position: shortCardEa.position,
                                viral_score: shortCardEa.viral_score ?? 0,
                                video_duration: shortCardEa.video_duration ?? segmentDuration,
                            });
                        }
                        const now = performance.now();
                        if (isFirstPlaybackTick || now - lastPlaybackUiFlushRef.current >= PLAYBACK_UI_THROTTLE_MS) {
                            isFirstPlaybackTick = false;
                            lastPlaybackUiFlushRef.current = now;
                            setCurrentTime(time);
                        }
                    },
                    (time: number) => {
                        setCurrentTime(time);
                        setIsPlaying(false);
                        setIsBuffering(false);
                        notifyExclusivePaused();
                    },
                );
                if (requestSeq !== playRequestSeqRef.current) {
                    await adapter.pause();
                    return;
                }
                setIsPlaying(true);
            } catch {
                setIsPlaying(false);
                setIsBuffering(false);
            } finally {
                if (requestSeq === playRequestSeqRef.current) {
                    setPlaybackStarting(false);
                }
            }
        })();
    }, [
        adapter,
        duration,
        detailsPlayback,
        playbackInstanceId,
        onBuffering,
        requestExclusivePlay,
        notifyExclusivePaused,
        onPlayStart,
        shortCardEa,
        segmentDuration,
    ]);

    const handlePause = useCallback(
        async (options?: { pauseMedia?: boolean; notifyContext?: boolean }) => {
            const pauseMedia = options?.pauseMedia ?? true;
            const notifyContext = options?.notifyContext ?? true;
            playRequestSeqRef.current += 1;
            setIsPlaying(false);
            setPlaybackStarting(false);
            setIsBuffering(false);
            if (pauseMedia) {
                await adapter.pause();
            } else {
                adapter.stopLocalPlaybackState?.();
            }
            if (notifyContext) {
                notifyExclusivePaused();
            }
        },
        [adapter, notifyExclusivePaused],
    );

    useEffect(() => {
        if (!onPauseControlReady) return;
        const pause = () => {
            void handlePause();
        };
        onPauseControlReady(pause);
        return () => {
            onPauseControlReady(() => {});
        };
    }, [handlePause, onPauseControlReady]);

    /** 详情页独占：全局 playing 已被其它片段占用时，本卡仅清本地 UI（共享解码器不能再 pause 全局媒体） */
    useEffect(() => {
        if (!exclusivePlayback || !playbackInstanceId || !detailsPlayback) return;
        const activeId = detailsPlayback.playingId;
        if (activeId && activeId !== playbackInstanceId && (isPlaying || isBuffering)) {
            queueMicrotask(() => {
                void handlePause({ pauseMedia: false, notifyContext: false });
            });
        }
    }, [
        detailsPlayback?.playingId,
        exclusivePlayback,
        playbackInstanceId,
        isPlaying,
        isBuffering,
        detailsPlayback,
        handlePause,
    ]);

    const notifyPausedFromContext = detailsPlayback?.notifyPaused;
    useEffect(() => {
        if (!exclusivePlayback || !playbackInstanceId || !notifyPausedFromContext) return;
        const id = playbackInstanceId;
        return () => {
            notifyPausedFromContext(id);
        };
    }, [exclusivePlayback, playbackInstanceId, notifyPausedFromContext]);

    useEffect(() => {
        const root = rootRef.current;
        return () => {
            void adapter.pause();
            if (root && document.fullscreenElement === root) {
                void document.exitFullscreen?.().catch(() => {});
            }
        };
    }, [adapter]);

    const handleTogglePlay = useCallback(() => {
        if (segmentDuration <= 0) return;
        logShortCardUserClick();
        if (isPlaying) void handlePause();
        else handlePlay();
    }, [handlePause, handlePlay, isPlaying, segmentDuration, logShortCardUserClick]);

    const handleFullscreen = useCallback(() => {
        const el = rootRef.current;
        if (!el) return;
        if (!document.fullscreenElement) {
            const prevPlayingId = detailsPlayback?.playingId ?? null;
            requestExclusivePlay();
            /** 从其它卡（如 A 正在播）切到本卡全屏时，必须真正 pause 共享片源；仅靠 context 会让 A 走 pauseMedia:false 停不掉底层 */
            if (prevPlayingId && prevPlayingId !== playbackInstanceId) {
                void adapter.pause();
            }
            el.requestFullscreen?.()
                ?.then(() => setIsFullscreen(true))
                .catch(() => {});
        } else {
            document
                .exitFullscreen?.()
                ?.then(() => setIsFullscreen(false))
                .catch(() => {});
        }
    }, [requestExclusivePlay, adapter, detailsPlayback, playbackInstanceId]);

    useEffect(() => {
        const onFullscreenChange = () => {
            const fs = !!document.fullscreenElement;
            setIsFullscreen(fs);
            queueMicrotask(() => {
                requestAnimationFrame(() => adapterRef.current?.resizeView());
            });
            if (!fs) {
                const active = document.activeElement;
                if (active instanceof HTMLButtonElement && rootRef.current?.contains(active)) {
                    active.blur();
                }
            }
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, [adapterRef]);

    useEffect(() => {
        if (!isFullscreen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== ' ' && e.code !== 'Space') return;
            const root = rootRef.current;
            if (!root || document.fullscreenElement !== root) return;
            const t = e.target as HTMLElement | null;
            if (t?.closest('textarea, select, [contenteditable="true"], input:not([type="range"])')) return;
            e.preventDefault();
            handleTogglePlay();
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [isFullscreen, handleTogglePlay]);

    const updateFullscreenFit = useCallback(() => {
        if (!isFullscreen) return;
        const el = videoAreaRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.width < 4 || r.height < 4) return;
        const scale = Math.min(r.width / contentW, r.height / contentH);
        const w = Math.max(2, Math.floor(contentW * scale));
        const h = Math.max(2, Math.floor(contentH * scale));
        setFitBox(prev => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
    }, [isFullscreen, contentW, contentH]);

    useEffect(() => {
        if (!isFullscreen || duration <= 0) return;
        const el = videoAreaRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => updateFullscreenFit());
        ro.observe(el);
        updateFullscreenFit();
        return () => ro.disconnect();
    }, [isFullscreen, duration, updateFullscreenFit]);

    useEffect(() => {
        if (isFullscreen && duration > 0) return;
        queueMicrotask(() => setFitBox(null));
    }, [isFullscreen, duration]);

    useLayoutEffect(() => {
        if (duration <= 0) return;
        if (isFullscreen && (!fitBox || fitBox.w < 2)) return;
        const id = requestAnimationFrame(() => {
            adapter.resizeView();
        });
        return () => cancelAnimationFrame(id);
    }, [adapter, isFullscreen, fitBox, duration, isFillLayout, fitW, fitH]);

    /**
     * 拖动进度条：本卡正在播时仅 seek，不改变播放态；否则（暂停 scrub / 从其它卡切过来）先 pause 再定位，避免误起播。
     */
    const handleSeek = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const time = Number(e.target.value);
            const prevPlayingId = detailsPlayback?.playingId ?? null;
            if (prevPlayingId !== playbackInstanceId) {
                requestExclusivePlay();
            }
            if (!Number.isFinite(time) || time < 0 || time > segmentDuration) return;

            const scrubWhileSelfPlaying = isPlaying && (!exclusivePlayback || prevPlayingId === playbackInstanceId);
            if (scrubWhileSelfPlaying) {
                void adapter.seek(time).then(() => {
                    setCurrentTime(time);
                    adapter.drawVideoCanvas();
                });
                return;
            }

            playRequestSeqRef.current += 1;
            setIsPlaying(false);
            setPlaybackStarting(false);
            setIsBuffering(false);

            void (async () => {
                await adapter.pause();
                await adapter.seek(time);
                await adapter.pause();
                setCurrentTime(time);
                adapter.drawVideoCanvas();
                notifyExclusivePaused();
            })();
        },
        [
            adapter,
            segmentDuration,
            playbackInstanceId,
            detailsPlayback,
            requestExclusivePlay,
            notifyExclusivePaused,
            isPlaying,
            exclusivePlayback,
        ],
    );

    const clampedTime = Math.min(currentTime, Math.max(0, segmentDuration));
    const progress = segmentDuration > 0 ? Math.min(clampedTime / segmentDuration, 1) : 0;

    const atSegmentStart = segmentDuration <= 0 || clampedTime < AT_SEGMENT_START_EPS;
    const decodeVisibleOverPoster = isPlaying && !isBuffering;
    const showPosterCover = posterSrc.length > 0 && !posterLoadError && atSegmentStart && !decodeVisibleOverPoster;

    const rootInlineStyle: CSSProperties | undefined =
        duration <= 0
            ? undefined
            : isFullscreen
              ? undefined
              : isFillLayout
                ? undefined
                : {
                      width: contentW,
                      height: contentH,
                      maxWidth: '100%',
                      maxHeight: maxPreview,
                      aspectRatio: `${contentW} / ${contentH}`,
                  };

    const canvasFitStyle: CSSProperties =
        isFullscreen && fitBox && fitBox.w >= 2
            ? { width: fitBox.w, height: fitBox.h }
            : isFillLayout
              ? { width: fitW, height: fitH }
              : { width: '100%', height: '100%' };

    const showBufferingHud = isBuffering && (isPlaying || playbackStarting);
    const showCenterHud = segmentDuration > 0 && !isPlaying && !showBufferingHud;

    return (
        <div
            ref={rootRef}
            className={classNames(
                styles.root,
                isFillLayout && styles.root_fill,
                isPlaying && styles.controlsVisible,
                isFullscreen && styles.fullscreen,
            )}
            style={rootInlineStyle}
        >
            <div
                ref={videoAreaRef}
                className={classNames(styles.video_area, isFillLayout && styles.video_area_fill)}
                onClick={handleTogglePlay}
            >
                <div
                    className={classNames(styles.canvas_fit, isFillLayout && styles.canvas_fit_fill)}
                    style={canvasFitStyle}
                >
                    {posterSrc ? (
                        <img
                            className={styles.poster_cover}
                            src={posterSrc}
                            alt=''
                            draggable={false}
                            aria-hidden
                            onError={() => setPosterLoadError(true)}
                        />
                    ) : null}
                    <canvas
                        ref={canvasRef}
                        className={classNames(styles.canvas, showPosterCover && styles.canvas_hide_for_poster)}
                        id='preview-video-canvas'
                    />
                    <div ref={subtitleContainerRef} className={styles.subtitle_overlay} />
                </div>
                {showCenterHud && (
                    <div className={styles.center_hud} aria-hidden>
                        <div className={styles.center_play_disc}>
                            <PlayBigIcon className={styles.center_play_svg} aria-hidden />
                        </div>
                        <div className={styles.center_duration}>{formatTime(segmentDuration)}</div>
                    </div>
                )}
                {showBufferingHud && (
                    <div className={styles.buffering_overlay} aria-busy aria-live='polite'>
                        <FJLoading style={{ width: 32, height: 32 }} strokeColor='rgba(255,255,255,0.92)' />
                    </div>
                )}
            </div>

            <div className={styles.controls} onClick={e => e.stopPropagation()}>
                <FJVideoController
                    segmentDuration={segmentDuration}
                    clampedTime={clampedTime}
                    progress={progress}
                    timeLabel={`${formatTime(clampedTime)} / ${formatTime(segmentDuration)}`}
                    isPlaying={isPlaying}
                    isFullscreen={isFullscreen}
                    onTogglePlayPause={() => {
                        logShortCardUserClick();
                        if (isPlaying) void handlePause();
                        else handlePlay();
                    }}
                    onSeek={handleSeek}
                    onToggleFullscreen={handleFullscreen}
                />
            </div>
        </div>
    );
}
